import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingState, Row } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { createDataMapById, flattenDataByKeys } from "@/utils/datamaps";
import {
  PartnerContactColumn,
  PartnerContactData
} from "@/pages/partners/contacts/types";
import { usePartnerContacts } from "@/pages/partners/contacts/hooks";
import { usePartnerContactColumns } from "@/pages/partners/contacts/columns";
import { usePartners } from "@/pages/partners/hooks";
import { PartnerData } from "@/pages/partners/types";
import EmptyState from "./EmptyState";
import Text from "@/components/ui/text";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import { useEntityFields, useFieldVisibility } from "@/pages/settings/hooks";
import { ColumnData } from "@/entities/dataTables/types";
import { UserData } from "@/entities/users/types";

const EMPTY_SELECTED_COLUMNS: ColumnData[] = [];

interface Props {
  partnerContactIds: string[];
  entityType: EntityType;
  onAddPartnerContact: () => void;
  userData?: UserData;
  className?: string;
}

const PartnerContactsTable: React.FC<Props> = ({
  partnerContactIds,
  entityType,
  onAddPartnerContact,
  userData,
  className
}) => {
  const { t } = useTranslation();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const [sorting, onSortingChange] = useState<SortingState>([
    { id: PartnerContactColumn.FULL_NAME, desc: false }
  ]);

  const {
    entities: fetchedContacts,
    isSuccess,
    isLoading: isFetching,
    missingIds
  } = usePartnerContacts(partnerContactIds, {
    enabled: partnerContactIds.length > 0,
    computed_fields: ComputedFields.MINIMAL
  });

  // Bounded by the fetch so a partial (206) response settles; skipped once every
  // contact is shown so a background refetch doesn't blur the rows.
  const isLoading = isFetching && !isSuccess;

  const partnerContacts = useMemo(
    () =>
      (fetchedContacts ?? []).filter(
        (contact): contact is PartnerContactData => Boolean(contact)
      ),
    [fetchedContacts]
  );

  const partnerIds: string[] = useMemo(
    () =>
      flattenDataByKeys(partnerContacts, (contact: PartnerContactData) => [
        contact.partner
      ]),
    [partnerContacts]
  );
  const partnersData = usePartners(partnerIds);
  const partnersDataMap = useMemo(
    () =>
      partnersData.entities
        ? createDataMapById(partnersData.entities as PartnerData[])
        : {},
    [partnersData.entities]
  );

  const { data: entityFieldsResponse } = useEntityFields(
    EntityType.PARTNER_CONTACT
  );
  const customFields = entityFieldsResponse?.fields || [];
  const {
    isFieldVisible,
    isLoaded: fieldsLoaded
  } = useFieldVisibility(EntityType.PARTNER_CONTACT);

  const columns = usePartnerContactColumns({
    partnersDataMap,
    selectedColumns: EMPTY_SELECTED_COLUMNS,
    customFields,
    isFieldVisible,
    fieldsLoaded
  });

  const onRowClick = (row: Row<PartnerContactData>) => {
    newOpenDrawer(row.original.id, EntityType.PARTNER_CONTACT);
  };

  if (partnerContactIds.length === 0) {
    return (
      <EmptyState onAddPeople={onAddPartnerContact} entityType={entityType} />
    );
  }

  return (
    <>
      {missingIds.length > 0 && (
        <Text variant="smBodySecondary" className="px-4 pb-2">
          {t("detailedView.peopleTab.unavailablePartnerContacts", {
            count: missingIds.length
          })}
        </Text>
      )}
      <DataTable
        key="partner-contacts-table"
        dataTest="partner-contacts-table"
        columns={columns}
        initialSortBy={sorting}
        onSortingChange={onSortingChange}
        data={partnerContacts}
        headerClassName="border-none"
        stickyLeftColumn
        resizableColumns
        className={className}
        isDataLoading={isLoading}
        onRowClick={onRowClick}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.PARTNER_CONTACT}
        onClose={newCloseDrawer}
        userData={userData}
        isTabView
      />
    </>
  );
};

export default PartnerContactsTable;
